import { api } from '@/lib/api/client';
import { ApiError } from '@/lib/api/errors';
import type {
  XkEvent,
  XkEventGroup,
  XkStation,
  XkStationSummary,
} from '@/lib/api/types';

/**
 * Backend list endpoints filter out `status='Deleted'` rows automatically,
 * but single-entity GETs (`/xk-event/:id`, etc.) do NOT. To keep soft-deleted
 * entities invisible everywhere, every `get*` below runs the response
 * through this guard.
 */
function assertNotDeleted<T extends { status?: string }>(entity: T, kind: string): T {
  if (entity?.status === 'Deleted') {
    throw new ApiError({
      message: `${kind} has been deleted`,
      status: 404,
      code: 'NOT_FOUND',
    });
  }
  return entity;
}

/**
 * Soft-delete convention used by all delete / remove methods:
 *
 * The backend `DELETE /resource/:id` endpoints route through `deleteMany`,
 * which calls `getOne` with the company filter applied. That blocks Agency
 * users from soft-deleting entities owned by their sub-brands (the brand's
 * `companyId` ≠ the agency user's `companyId`).
 *
 * To work around this WITHOUT a backend change, we issue `PUT /resource/:id`
 * with `{ status: 'Deleted' }`. The update path uses `repository.update(...)`
 * directly (no `getOne` guard), and `BaseService.applyFilters` already hides
 * rows where `status = 'Deleted'` from every list/findOne query — so the
 * entity disappears from listings just like a real delete would.
 *
 * If/when the backend's `getOne` learns to accept Agency users (e.g. via
 * `co_company_accesses`), these can flip back to real `DELETE` calls.
 */
export const eventsApi = {
  listGroups: (filter?: Record<string, unknown>) =>
    api.list<XkEventGroup>('/xk-event-group', { filter }),
  getGroup: (id: number) =>
    api.get<XkEventGroup>(`/xk-event-group/${id}`).then((g) => assertNotDeleted(g, 'Project')),
  createGroup: (input: Partial<XkEventGroup>) =>
    api.post<XkEventGroup>('/xk-event-group', input),
  updateGroup: (id: number, patch: Partial<XkEventGroup>) =>
    api.put<XkEventGroup>(`/xk-event-group/${id}`, patch),
  deleteGroup: (id: number) =>
    api.put<XkEventGroup>(`/xk-event-group/${id}`, { status: 'Deleted' }),

  listEvents: (filter?: Record<string, unknown>) =>
    api.list<XkEvent>('/xk-event', { filter }),
  getEvent: (id: number) =>
    api.get<XkEvent>(`/xk-event/${id}`).then((e) => assertNotDeleted(e, 'Event')),
  createEvent: (input: Partial<XkEvent>) => api.post<XkEvent>('/xk-event', input),
  /**
   * Batch-create N events under a project in a single call. The backend
   * auto-numbers names (Event1..EventN), spreads dates across the project
   * range, and gives each event one default station. Plan caps are enforced
   * line-by-line — the first overflow aborts with 409.
   */
  createEventsBatch: (input: {
    eventGroupId: number;
    count: number;
    companyId?: number;
    address?: string;
    sectionCount?: number;
  }) =>
    api.post<{ created: number; items: XkEvent[] }>('/xk-event/batch', input),
  updateEvent: (id: number, patch: Partial<XkEvent>) =>
    api.put<XkEvent>(`/xk-event/${id}`, patch),
  deleteEvent: (id: number) =>
    api.put<XkEvent>(`/xk-event/${id}`, { status: 'Deleted' }),

  listStations: (filter?: Record<string, unknown>) =>
    api.list<XkStation>('/xk-station', { filter }),
  getStation: (id: number) =>
    api.get<XkStation>(`/xk-station/${id}`).then((s) => assertNotDeleted(s, 'Station')),
  createStation: (input: Partial<XkStation>) =>
    api.post<XkStation>('/xk-station', input),
  updateStation: (id: number, patch: Partial<XkStation>) =>
    api.put<XkStation>(`/xk-station/${id}`, patch),
  deleteStation: (id: number) =>
    api.put<XkStation>(`/xk-station/${id}`, { status: 'Deleted' }),
  /**
   * Clone an existing station (with all its stock allocations) into another
   * event. Backend resets `available = quantity` on each clone. Returns the
   * newly created station.
   */
  cloneStation: (input: {
    id: number;
    surveyId: number;
    eventSelectId: number;
    name: string;
  }) => api.post<XkStation>('/xk-station/clone', input),

  /**
   * Composite station-detail payload for the Station Detail page.
   * Backed by `GET /xk-station/:id/summary` — event + survey + inventory
   * with `used = quantity - available` derived, plus generic response
   * analytics (time series, top answers, last 24h/7d) from the shared
   * TouchpointInsightsService kernel.
   */
  getStationSummary: (id: number) =>
    api.get<XkStationSummary>(`/xk-station/${id}/summary`),

  /**
   * Paginated grouped survey responses (one entry per submission/responseId)
   * for the station's Responses tab. Delegates to the existing
   * `GET /xy-xurvey-response/grouped` which already filters by surveyId +
   * stationId at the SQL layer.
   */
  listStationResponses: (input: {
    surveyId: number;
    stationId: number;
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'ASC' | 'DESC';
    search?: string;
  }) =>
    api.get<{
      items: Array<{
        responseId: string;
        name?: string;
        email?: string;
        phone?: string;
        answerCount: number;
        source?: string;
        submittedAt: string;
        answers: Array<{
          componentId: number;
          label: string;
          type: string;
          value: string;
          stepId: number;
        }>;
      }>;
      total: number;
      totalAnswers: number;
      todayCount: number;
      sources: Record<string, number>;
    }>(`/xy-xurvey-response/grouped`, {
      params: {
        surveyId: input.surveyId,
        stationId: input.stationId,
        page: input.page ?? 1,
        limit: input.limit ?? 20,
        sort: input.sort ?? 'submittedAt',
        order: input.order ?? 'DESC',
        ...(input.search ? { search: input.search } : {}),
      },
    }),
};
